import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { Plus, BookOpen, ArrowRight } from 'lucide-react'
import ReadingCalendar from '@/components/ReadingCalendar'
import { UserAvatar } from '@/components/Avatar'

export default function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string>()
  const [showRecordDetail, setShowRecordDetail] = useState(false)

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    count: 1,
    bookTitle: '书籍A',
  })

  const { data: readingHistory } = useQuery({
    queryKey: ['readingHistory'],
    queryFn: () => api.getReadingHistory(),
    enabled: !!user,
  })

  const submitMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      api.submitReading(data.date, data.count, data.bookTitle, ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readingHistory'] })
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] })
      setShowAddForm(false)
      setFormData({
        date: new Date().toISOString().split('T')[0],
        count: 1,
        bookTitle: '书籍A',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitMutation.mutate(formData)
  }

  const handleDateSelect = (date: string) => {
    setSelectedDate(date)
    setShowRecordDetail(true)
  }

  const handleAddRecord = () => {
    setFormData(prev => ({ ...prev, date: selectedDate || new Date().toISOString().split('T')[0] }))
    setShowRecordDetail(false)
    setShowAddForm(true)
  }

  // 获取选中日期的记录
  const selectedDateRecord = selectedDate
    ? readingHistory?.data?.data?.find((record: any) => record.date === selectedDate)
    : null



  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* 轻盈背景装饰 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-10 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl"></div>
        </div>

        {/* 主要内容 */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8 max-w-xs mx-auto">

          {/* App图标 */}
          <div className="relative mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl flex items-center justify-center">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* 标题 */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            读书共修
          </h1>

          {/* 副标题 */}
          <p className="text-base text-gray-600 text-center mb-6 max-w-xs">
            记录阅读足迹，共同成长进步
          </p>

          {/* 功能特色 - 紧凑版 */}
          <div className="w-full grid grid-cols-3 gap-3 mb-6">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-xl p-3 text-center shadow-lg">
              <div className="text-2xl mb-1">📚</div>
              <div className="text-xs font-medium text-gray-700">记录阅读</div>
            </div>

            <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-xl p-3 text-center shadow-lg">
              <div className="text-2xl mb-1">📅</div>
              <div className="text-xs font-medium text-gray-700">日历视图</div>
            </div>

            <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-xl p-3 text-center shadow-lg">
              <div className="text-2xl mb-1">🌱</div>
              <div className="text-xs font-medium text-gray-700">共同成长</div>
            </div>
          </div>

          {/* 开始使用按钮 */}
          <div className="w-full space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-6 rounded-xl font-semibold shadow-xl transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <BookOpen className="h-4 w-4" />
              <span>开始记录</span>
              <ArrowRight className="h-4 w-4" />
            </button>

            {/* 提示信息 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center space-x-2 text-amber-700">
                <span>💡</span>
                <span className="text-xs font-medium">使用邀请码加入共修小组</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 计算今日和本周统计
  const today = new Date().toISOString().split('T')[0]
  const records = readingHistory?.data?.data || []
  const todayRecords = records.filter(r => r.date === today)
  const todayCount = todayRecords.reduce((sum, r) => sum + r.count, 0)

  const weekStart = new Date()
  weekStart.setDate(weekStart.getDate() - weekStart.getDay())
  const weekStartStr = weekStart.toISOString().split('T')[0]
  const weekRecords = records.filter(r => r.date >= weekStartStr)
  const weekCount = weekRecords.reduce((sum, r) => sum + r.count, 0)

  const totalCount = records.reduce((sum, r) => sum + r.count, 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* 欢迎区域 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-shrink-0">
              <UserAvatar
                src={user?.avatar}
                username={user?.username}
                userId={user?.id}
                size="lg"
                priority={true}
                lazy={false}
              />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900">
                你好，{user?.username}
              </h1>
              <p className="text-gray-600 text-sm">
                今天也要坚持读书哦 📚
              </p>
            </div>
          </div>

          {/* 快速统计 */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{todayCount}</div>
              <div className="text-xs text-gray-500">今日</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{weekCount}</div>
              <div className="text-xs text-gray-500">本周</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{totalCount}</div>
              <div className="text-xs text-gray-500">总计</div>
            </div>
          </div>

          <button
            onClick={() => setShowAddForm(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            添加读书记录
          </button>
        </div>





        {/* 查看记录详情弹窗 */}
        {showRecordDetail && selectedDate && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedDate} 的记录
                </h3>
                <p className="text-sm text-gray-600 mt-1">查看当日阅读记录</p>
              </div>

              {selectedDateRecord ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      {selectedDateRecord.count}
                    </div>
                    <div className="text-sm text-green-700">遍数</div>
                  </div>

                  {/* <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="text-sm text-gray-600 mb-1">书籍</div>
                    <div className="font-medium text-gray-900">
                      {selectedDateRecord.bookTitle || '书籍A'}
                    </div>
                  </div> */}

                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => setShowRecordDetail(false)}
                      className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
                    >
                      关闭
                    </button>
                    <button
                      onClick={handleAddRecord}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-lg"
                    >
                      ✏️ 修改
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                    <div className="text-gray-400 text-4xl mb-2">📖</div>
                    <div className="text-gray-600 text-sm">当日暂无阅读记录</div>
                  </div>

                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => setShowRecordDetail(false)}
                      className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
                    >
                      关闭
                    </button>
                    <button
                      onClick={handleAddRecord}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-lg"
                    >
                      ➕ 添加记录
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">记录读书遍数</h3>
                <p className="text-sm text-gray-600 mt-1">记录今日的阅读成果</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📅 日期
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white/80 backdrop-blur-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📖 读书遍数
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.count}
                    onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white/80 backdrop-blur-sm text-center text-lg font-semibold"
                    required
                    placeholder="1"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all disabled:opacity-50 shadow-lg"
                  >
                    {submitMutation.isPending ? '提交中...' : '✨ 提交'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* 日历视图 */}
      <ReadingCalendar
        records={readingHistory?.data?.data || []}
        onDateSelect={handleDateSelect}
        selectedDate={selectedDate}
      />
      </div>
    </div>
  )
}
